import React from 'react';
import { useTranslation } from 'react-i18next';
import { PanelMode } from './call-card.types';
import styles from './CallCard.module.css';

interface CallControlGridProps {
  isMuted: boolean;
  isOnHold: boolean;
  isLoading: string | null;
  isAgentLegActive: boolean;
  panelMode: PanelMode;
  isPanelOpen: boolean;
  onMute: () => void;
  onHold: () => void;
  onResume: () => void;
  onEnd: () => void;
  onTransferClick: () => void;
  onConferenceClick: () => void;
}

export function CallControlGrid({
  isMuted,
  isOnHold,
  isLoading,
  isAgentLegActive,
  panelMode,
  isPanelOpen,
  onMute,
  onHold,
  onResume,
  onEnd,
  onTransferClick,
  onConferenceClick,
}: CallControlGridProps) {
  const { t } = useTranslation();
  const disabledTitle = !isAgentLegActive ? 'Connecting...' : undefined;

  return (
    <div className={styles.callControlsGrid}>
      <button
        className={`${styles.actionBtn} ${styles.btnMute} ${isMuted ? styles.muted : ''}`}
        onClick={onMute}
        disabled={isLoading !== null || !isAgentLegActive}
        title={disabledTitle}
      >
        {isMuted ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="1" y1="1" x2="23" y2="23" />
            <path d="M9 9v3a3 3 0 005.12 2.12M15 9.34V4a3 3 0 00-5.94-.6" />
            <path d="M17 16.95A7 7 0 015 12v-2m14 0v2a7 7 0 01-.11 1.23" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
            <path d="M19 10v2a7 7 0 01-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
        )}
        <span>{isMuted ? t('call:actions.unmute') : t('call:actions.mute')}</span>
      </button>

      <button
        className={`${styles.actionBtn} ${styles.btnHold} ${isOnHold ? styles.onHold : ''}`}
        onClick={isOnHold ? onResume : onHold}
        disabled={isLoading !== null || !isAgentLegActive}
        title={disabledTitle}
      >
        {isOnHold ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="5 3 19 12 5 21 5 3" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="6" y="4" width="4" height="16" />
            <rect x="14" y="4" width="4" height="16" />
          </svg>
        )}
        <span>{isOnHold ? t('call:actions.resume') : t('call:actions.hold')}</span>
      </button>

      <button
        className={`${styles.actionBtn} ${styles.btnConference} ${panelMode === 'conference' ? styles.active : ''}`}
        onClick={onConferenceClick}
        disabled={isLoading !== null || isPanelOpen || !isAgentLegActive}
        title={disabledTitle}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 00-3-3.87" />
          <path d="M16 3.13a4 4 0 010 7.75" />
        </svg>
        <span>{t('call:actions.conference')}</span>
      </button>

      <button
        className={`${styles.actionBtn} ${styles.btnTransfer} ${panelMode === 'transfer' ? styles.active : ''}`}
        onClick={onTransferClick}
        disabled={isLoading !== null || isPanelOpen || !isAgentLegActive}
        title={disabledTitle}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
          <polyline points="15 3 21 3 21 9" />
          <line x1="10" y1="14" x2="21" y2="3" />
        </svg>
        <span>{t('call:actions.transfer')}</span>
      </button>

      <button
        className={`${styles.actionBtn} ${styles.btnDrop}`}
        onClick={onEnd}
        disabled={isLoading !== null}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M10.68 13.31a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92V20a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07M1 1l22 22M16.56 2a19.79 19.79 0 01.56 3.67A2 2 0 0115.32 8l-1.27 1.27" />
          <path d="M4.22 4.22A19.79 19.79 0 002 19.82 2 2 0 004.11 22h3a2 2 0 002-1.72" />
        </svg>
        {t('call:actions.endCall')}
      </button>
    </div>
  );
}
